import { api } from './api';
import type {
  DocumentTaxonomy,
  ReviewChange,
  SuggestionRun,
  TaxonomyAssignment,
} from '@/types/taxonomy';

interface ApiAssignment {
  term_id: string;
  code: string | null;
  facet: TaxonomyAssignment['facet'];
  display_name_ja: string | null;
  display_name_en: string | null;
  term_status: TaxonomyAssignment['termStatus'];
  replacement_term_id: string | null;
  taxonomy_version_id: string | null;
  source: TaxonomyAssignment['source'];
  state: TaxonomyAssignment['state'];
  confidence: number | null;
  generator_version: string | null;
  reason_code: string | null;
}

interface ApiSuggestionRun {
  status: SuggestionRun['status'];
  attempt_count: number;
  candidate_count: number | null;
  error_code: SuggestionRun['errorCode'];
  generator_version: string | null;
  started_at: string | null;
  finished_at: string | null;
  retryable: boolean;
}

interface ApiDocumentTaxonomy {
  document_id: string;
  taxonomy_version: number | null;
  confirmed: ApiAssignment[];
  migrated: ApiAssignment[];
  suggested: ApiAssignment[];
  suggestion_run: ApiSuggestionRun;
}

interface ApiRetryResponse {
  document_id: string;
  suggestion_run: ApiSuggestionRun;
}

const mapAssignment = (row: ApiAssignment): TaxonomyAssignment => ({
  termId: row.term_id,
  code: row.code,
  facet: row.facet,
  displayNameJa: row.display_name_ja,
  displayNameEn: row.display_name_en,
  termStatus: row.term_status,
  replacementTermId: row.replacement_term_id,
  taxonomyVersionId: row.taxonomy_version_id,
  source: row.source,
  state: row.state,
  confidence: row.confidence,
  generatorVersion: row.generator_version,
  reasonCode: row.reason_code,
});

const mapRun = (run: ApiSuggestionRun): SuggestionRun => ({
  status: run.status,
  attemptCount: run.attempt_count,
  candidateCount: run.candidate_count,
  errorCode: run.error_code,
  generatorVersion: run.generator_version,
  startedAt: run.started_at,
  finishedAt: run.finished_at,
  retryable: run.retryable,
});

const mapTaxonomy = (body: ApiDocumentTaxonomy): DocumentTaxonomy => ({
  documentId: body.document_id,
  taxonomyVersion: body.taxonomy_version,
  confirmed: body.confirmed.map(mapAssignment),
  migrated: body.migrated.map(mapAssignment),
  suggested: body.suggested.map(mapAssignment),
  suggestionRun: mapRun(body.suggestion_run),
});

class TaxonomyService {
  async getDocumentTaxonomy(documentId: string): Promise<DocumentTaxonomy> {
    const response = await api.get<ApiDocumentTaxonomy>(
      `/documents/${documentId}/taxonomy`,
    );
    return mapTaxonomy(response);
  }

  /** Reject and add travel together: splitting them can leave a single-valued
   * facet with no value if the second call never happens. */
  async review(documentId: string, changes: ReviewChange[]): Promise<DocumentTaxonomy> {
    const response = await api.patch<ApiDocumentTaxonomy>(
      `/documents/${documentId}/taxonomy`,
      { changes },
    );
    return mapTaxonomy(response);
  }

  /** 202 with the claimed run, or 409 with `already_succeeded` / `run_in_progress`. */
  async retrySuggestions(documentId: string): Promise<SuggestionRun> {
    const response = await api.post<ApiRetryResponse>(
      `/documents/${documentId}/taxonomy/suggestions/retry`,
    );
    return mapRun(response.suggestion_run);
  }
}

export const taxonomyService = new TaxonomyService();
